import { CartAddEvent } from '@theme/events';

/**
 * Shared AJAX add-to-cart for cart-drawer custom features (upsell + FBT).
 * - `.js-cart-add`                       → single variant via `data-variant-id`
 * - `.js-cart-add[data-has-variants]`    → opens a variant-picker popup first
 * - `.js-cart-add-all`                   → multiple variants via `data-variant-ids`
 * - `.js-upsell-nav`                     → scrolls the desktop upsell carousel
 *
 * Mirrors the native product-form flow (assets/product-form.js): posts to
 * cart/add.js with `sections`, then dispatches CartAddEvent so
 * component-cart-items morphs the section.
 */

const TRIGGER_SELECTOR = '.js-cart-add, .js-cart-add-all';
const ERROR_RESET_MS = 2500;
const MORPH_SAFETY_MS = 1500;

/** @returns {string[]} unique cart-items-component section ids on the page */
function getCartSectionIds() {
  /** @type {string[]} */
  const ids = [];
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
    if (el instanceof HTMLElement && el.dataset.sectionId && !ids.includes(el.dataset.sectionId)) {
      ids.push(el.dataset.sectionId);
    }
  });
  return ids;
}

/**
 * Visually-hidden polite live region so screen readers hear add success/failure.
 * @param {string} message
 */
function announce(message) {
  let region = document.getElementById('cart-custom-features-status');
  if (!region) {
    region = document.createElement('div');
    region.id = 'cart-custom-features-status';
    region.setAttribute('aria-live', 'polite');
    region.setAttribute('role', 'status');
    region.className = 'visually-hidden';
    document.body.appendChild(region);
  }
  region.textContent = '';
  requestAnimationFrame(() => {
    region.textContent = message;
  });
}

/** @param {string} s */
function escapeHtml(s) {
  return String(s).replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])
  );
}

/**
 * @param {HTMLElement} button
 * @returns {{ id: string, quantity: number }[]}
 */
function itemsFromButton(button) {
  const multi = button.dataset.variantIds;
  const single = button.dataset.variantId;
  if (multi) {
    return multi
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean)
      .map((id) => ({ id, quantity: 1 }));
  }
  if (single) return [{ id: single.trim(), quantity: 1 }];
  return [];
}

/**
 * @param {HTMLElement} button
 * @param {Element|null} text
 * @param {Element|null} spinner
 * @param {boolean} busy
 */
function setBusy(button, text, spinner, busy) {
  if (busy) {
    button.setAttribute('aria-busy', 'true');
    button.setAttribute('disabled', 'true');
    if (text instanceof HTMLElement) text.hidden = true;
    if (spinner instanceof HTMLElement) spinner.hidden = false;
  } else {
    button.removeAttribute('aria-busy');
    button.removeAttribute('disabled');
    if (spinner instanceof HTMLElement) spinner.hidden = true;
    if (text instanceof HTMLElement) text.hidden = false;
  }
}

/**
 * Core add: POST cart/add.js (with section refresh). Throws on a Shopify error
 * (cart/add is atomic — if any line fails nothing is added).
 * @param {{ id: string, quantity: number }[]} items
 */
async function submitAdd(items) {
  const formData = new FormData();
  items.forEach((item, index) => {
    formData.append(`items[${index}][id]`, item.id);
    formData.append(`items[${index}][quantity]`, String(item.quantity));
  });
  const sectionIds = getCartSectionIds();
  if (sectionIds.length) formData.append('sections', sectionIds.join(','));
  formData.append('sections_url', window.location.pathname);

  const response = await fetch(Theme.routes.cart_add_url, {
    method: 'POST',
    headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'text/html' },
    body: formData,
  });
  const data = await response.json();
  if (data.status) throw new Error(data.description || data.message || 'cart/add failed');
  return data;
}

/**
 * Tell the drawer to morph (matches product-form flow). The delay lets a success
 * state show briefly before the section re-renders.
 * @param {any} data
 * @param {number} itemCount
 * @param {number} [delay]
 */
function notifyCartAdded(data, itemCount, delay = 700) {
  setTimeout(() => {
    document.dispatchEvent(
      new CartAddEvent({}, 'cart-custom-features', {
        source: 'product-form-component',
        itemCount,
        sections: data.sections,
      })
    );
  }, delay);
}

/** @param {HTMLElement} button */
async function addItems(button) {
  const items = itemsFromButton(button);
  if (!items.length || button.hasAttribute('aria-busy')) return;

  const text = button.querySelector('.cart-add__text');
  const spinner = button.querySelector('.cart-add__spinner');
  // Capture the idle label ONCE so retries never persist a transient state label.
  const idleLabel = text instanceof HTMLElement ? text.textContent : '';

  setBusy(button, text, spinner, true);

  try {
    const data = await submitAdd(items);

    // Feedback: "✓ Adicionado" — drop the disabled grey, keep aria-busy as the
    // re-click guard until either the morph removes the button or the safety reset.
    button.removeAttribute('disabled');
    if (spinner instanceof HTMLElement) spinner.hidden = true;
    if (text instanceof HTMLElement) {
      text.hidden = false;
      text.textContent = '✓ Adicionado';
    }
    button.classList.add('is-added');
    announce('Produto adicionado ao carrinho.');

    notifyCartAdded(data, items.length);

    // Safety net: if the morph did NOT remove the button, restore a usable state.
    setTimeout(() => {
      if (!button.isConnected) return;
      button.classList.remove('is-added');
      if (text instanceof HTMLElement) text.textContent = idleLabel;
      setBusy(button, text, spinner, false);
    }, MORPH_SAFETY_MS);
  } catch (error) {
    console.error('[cart-custom-features]', error);
    announce('Não foi possível adicionar. Tente novamente.');
    if (spinner instanceof HTMLElement) spinner.hidden = true;
    if (text instanceof HTMLElement) {
      text.hidden = false;
      text.textContent = 'Indisponível';
    }
    setTimeout(() => {
      if (text instanceof HTMLElement) text.textContent = idleLabel;
      setBusy(button, text, spinner, false);
    }, ERROR_RESET_MS);
  }
}

/* ============================ Variant picker popup ============================ */

/** @param {KeyboardEvent} event */
function onPickerKeydown(event) {
  if (event.key === 'Escape') closeVariantPicker();
}

function closeVariantPicker() {
  document.removeEventListener('keydown', onPickerKeydown);
  const picker = document.querySelector('.cart-upsell-picker');
  if (!(picker instanceof HTMLElement)) return;
  picker.classList.remove('is-open');
  // Let the exit transition play, then remove.
  setTimeout(() => picker.remove(), 220);
}

/**
 * Multi-variant upsell: show a popup to pick the variant before adding.
 * Variant data is embedded in the card as `<script.js-upsell-variants>` JSON.
 * The popup is appended INSIDE the cart drawer <dialog> so it stacks above the
 * drawer (same top layer) and is scoped to the drawer panel.
 * @param {HTMLElement} button
 */
function openVariantPicker(button) {
  const card = button.closest('.cart-upsell__card');
  const dataEl = card?.querySelector('.js-upsell-variants');
  /** @type {{id:string,title:string,price:string,available:boolean}[]} */
  let variants = [];
  if (dataEl) {
    try {
      variants = JSON.parse(dataEl.textContent || '[]');
    } catch {
      variants = [];
    }
  }
  // No usable data → fall back to adding the default variant.
  if (!variants.length) return addItems(button);

  closeVariantPicker();

  const title = button.dataset.productTitle || '';
  const picker = document.createElement('div');
  picker.className = 'cart-upsell-picker';
  picker.setAttribute('role', 'dialog');
  picker.setAttribute('aria-modal', 'true');
  picker.setAttribute('aria-label', `Escolha a opção: ${title}`);
  picker.innerHTML = `
    <div class="cart-upsell-picker__backdrop" data-picker-close></div>
    <div class="cart-upsell-picker__sheet" role="document">
      <div class="cart-upsell-picker__head">
        <span class="cart-upsell-picker__title">${escapeHtml(title)}</span>
        <button type="button" class="cart-upsell-picker__close" data-picker-close aria-label="Fechar">
          <svg aria-hidden="true" focusable="false" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
        </button>
      </div>
      <p class="cart-upsell-picker__subtitle">Escolha a opção</p>
      <ul class="cart-upsell-picker__list">
        ${variants
          .map(
            (v) => `
          <li>
            <button type="button" class="cart-upsell-picker__opt" data-id="${escapeHtml(v.id)}" ${
              v.available ? '' : 'disabled'
            }>
              <span class="cart-upsell-picker__opt-title">${escapeHtml(v.title)}</span>
              <span class="cart-upsell-picker__opt-price">${v.available ? escapeHtml(v.price) : 'Esgotado'}</span>
            </button>
          </li>`
          )
          .join('')}
      </ul>
    </div>`;

  const host = button.closest('dialog') || document.body;
  host.appendChild(picker);
  requestAnimationFrame(() => picker.classList.add('is-open'));
  document.addEventListener('keydown', onPickerKeydown);

  picker.addEventListener('click', async (event) => {
    const el = event.target instanceof Element ? event.target : null;
    if (!el) return;
    if (el.closest('[data-picker-close]')) {
      closeVariantPicker();
      return;
    }
    const opt = el.closest('.cart-upsell-picker__opt');
    if (!(opt instanceof HTMLElement) || opt.hasAttribute('disabled') || opt.hasAttribute('aria-busy')) return;

    opt.setAttribute('aria-busy', 'true');
    opt.classList.add('is-loading');
    try {
      const data = await submitAdd([{ id: String(opt.dataset.id), quantity: 1 }]);
      announce('Produto adicionado ao carrinho.');
      notifyCartAdded(data, 1, 250);
      closeVariantPicker();
    } catch (error) {
      console.error('[cart-custom-features] picker add', error);
      opt.classList.remove('is-loading');
      opt.removeAttribute('aria-busy');
      const priceEl = opt.querySelector('.cart-upsell-picker__opt-price');
      if (priceEl) priceEl.textContent = 'Indisponível';
    }
  });
}

/* ============================ Upsell carousel nav ============================ */

/** Toggle the desktop arrows based on overflow + scroll position (per viewport). */
function updateUpsellNav() {
  document.querySelectorAll('.cart-upsell__viewport').forEach((vp) => {
    const carousel = vp.querySelector('.cart-upsell__carousel');
    const prev = vp.querySelector('.cart-upsell__nav--prev');
    const next = vp.querySelector('.cart-upsell__nav--next');
    if (!(carousel instanceof HTMLElement)) return;
    const max = carousel.scrollWidth - carousel.clientWidth;
    const overflow = max > 4;
    if (prev instanceof HTMLElement) prev.hidden = !overflow || carousel.scrollLeft <= 2;
    if (next instanceof HTMLElement) next.hidden = !overflow || carousel.scrollLeft >= max - 2;
  });
}

/** @type {ResizeObserver | null} */
let resizeObserver = null;
/** Observe carousels so the arrows re-evaluate when the drawer opens (0→real width) or on resize. */
function observeCarousels() {
  if (typeof ResizeObserver === 'undefined') {
    updateUpsellNav();
    return;
  }
  if (!resizeObserver) resizeObserver = new ResizeObserver(() => updateUpsellNav());
  document.querySelectorAll('.cart-upsell__carousel').forEach((c) => resizeObserver?.observe(c));
  updateUpsellNav();
}

/** @param {HTMLElement} navBtn */
function scrollCarousel(navBtn) {
  const carousel = navBtn.closest('.cart-upsell__viewport')?.querySelector('.cart-upsell__carousel');
  if (!(carousel instanceof HTMLElement)) return;
  const dir = Number(navBtn.dataset.dir) || 1;
  const card = carousel.querySelector('.cart-upsell__card');
  const step = card instanceof HTMLElement ? card.getBoundingClientRect().width + 8 : carousel.clientWidth * 0.85;
  carousel.scrollBy({ left: dir * step, behavior: 'smooth' });
}

/* ================================ Wiring ================================ */

if (!window.__cartCustomFeatures) {
  window.__cartCustomFeatures = true;

  // Delegated listener survives section morphs (no per-button re-init needed).
  document.addEventListener('click', (event) => {
    const el = event.target instanceof Element ? event.target : null;
    if (!el) return;

    const nav = el.closest('.js-upsell-nav');
    if (nav instanceof HTMLElement) {
      event.preventDefault();
      scrollCarousel(nav);
      return;
    }

    const trigger = el.closest(TRIGGER_SELECTOR);
    if (!(trigger instanceof HTMLElement)) return;
    event.preventDefault();
    if (trigger.dataset.hasVariants === 'true') {
      openVariantPicker(trigger);
    } else {
      addItems(trigger);
    }
  });

  // Recompute arrow visibility when the carousel scrolls (scroll doesn't bubble →
  // capture), on resize, and after the section morphs (re-observe the new carousel).
  document.addEventListener(
    'scroll',
    (event) => {
      if (event.target instanceof Element && event.target.classList.contains('cart-upsell__carousel')) {
        updateUpsellNav();
      }
    },
    true
  );
  window.addEventListener('resize', updateUpsellNav);
  document.addEventListener(CartAddEvent.eventName, () => setTimeout(observeCarousels, 80));

  const init = () => observeCarousels();
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
}
